import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import {
  WhatsAppConfig,
  CreateWhatsAppConfigRequest,
  UpdateWhatsAppConfigRequest,
  WhatsAppEmbeddedSignupConfig,
  CreateEmbeddedSignupConfigRequest,
  UpdateEmbeddedSignupConfigRequest,
  WhatsAppTemplate,
  CreateTemplateRequest,
  WhatsAppCampaign,
  TemplateInsights,
  CampaignInsights,
  CreateCampaignRequest,
  UpdateCampaignRequest,
  WhatsAppMessage,
  SendMessageRequest,
  WhatsAppSendMessageRequest,
  WhatsAppSendTextRequest,
  WhatsAppSendTemplateRequest,
  WhatsAppHourlyReport,
  WhatsAppResyncResponse,
  WhatsAppDisconnectResponse,
  WhatsAppReactivateResponse,
  WABARegistration,
  LinkCreditLineRequest,
  LinkCreditLineResponse,
  RegisterPhoneNumberRequest,
  RegisterPhoneNumberResponse,
  WebhookSubscriptionResponse,
  ApiResponse,
  PaginatedResponse,
} from 'types';

/**
 * WhatsApp Service
 * Connects to the backend WhatsApp API for CPaaS platform
 */
class WhatsAppService {
  // ==================== Configuration ====================

  /**
   * Get WhatsApp configuration for current user's organization
   */
  async getConfig(): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONFIG);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch WhatsApp configuration',
      };
    }
  }

  /**
   * Get WhatsApp configuration for specific organization
   */
  async getConfigForOrganization(organizationId: string): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONFIG_ORG(organizationId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch configuration',
      };
    }
  }

  /**
   * Get all business configurations (for providers)
   */
  async getBusinessConfigs(): Promise<ApiResponse<WhatsAppConfig[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONFIG_BUSINESSES);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch business configurations',
      };
    }
  }

  /**
   * Create WhatsApp configuration
   */
  async createConfig(request: CreateWhatsAppConfigRequest): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Configuration created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create configuration',
      };
    }
  }

  /**
   * Update WhatsApp configuration
   */
  async updateConfig(request: UpdateWhatsAppConfigRequest): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const response = await api.put(API_ENDPOINTS.WHATSAPP_CONFIG, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Configuration updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update configuration',
      };
    }
  }

  /**
   * Delete WhatsApp configuration (primary/default)
   */
  async deleteConfig(): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(API_ENDPOINTS.WHATSAPP_CONFIG);
      return {
        success: response.data.success,
        message: response.data.message || 'Configuration deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete configuration',
      };
    }
  }

  /**
   * Get ALL WhatsApp configurations for current user's organization.
   * Supports multiple WhatsApp numbers per organization.
   */
  async getAllConfigs(): Promise<ApiResponse<WhatsAppConfig[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONFIG_ALL);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch configurations',
      };
    }
  }

  /**
   * Get ALL WhatsApp configurations for specific organization.
   */
  async getAllConfigsForOrganization(organizationId: string): Promise<ApiResponse<WhatsAppConfig[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONFIG_ALL_ORG(organizationId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch configurations',
      };
    }
  }

  /**
   * Update specific WhatsApp configuration by ID.
   * Use this for updating specific numbers when organization has multiple.
   */
  async updateConfigById(configId: string, request: UpdateWhatsAppConfigRequest): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const response = await api.put(API_ENDPOINTS.WHATSAPP_CONFIG_BY_ID(configId), request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Configuration updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update configuration',
      };
    }
  }

  /**
   * Delete specific WhatsApp configuration by ID.
   * Use this for deleting specific numbers when organization has multiple.
   */
  async deleteConfigById(configId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(API_ENDPOINTS.WHATSAPP_CONFIG_BY_ID(configId));
      return {
        success: response.data.success,
        message: response.data.message || 'Configuration deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete configuration',
      };
    }
  }

  /**
   * Register a phone number with Meta
   * Requires a 6-digit PIN for 2FA
   */
  async registerPhoneNumber(request: RegisterPhoneNumberRequest): Promise<ApiResponse<RegisterPhoneNumberResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG_REGISTER, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Phone number registered successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to register phone number',
      };
    }
  }

  /**
   * Register a phone number by config ID
   * Convenience method using path parameter
   */
  async registerPhoneNumberByConfigId(configId: string, pin: string): Promise<ApiResponse<RegisterPhoneNumberResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG_REGISTER_BY_ID(configId), null, {
        params: { pin },
      });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Phone number registered successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to register phone number',
      };
    }
  }

  /**
   * Get embedded signup configuration with inheritance
   * Uses inheritance logic: checks org config -> parent org -> platform default
   */
  async getEmbeddedSignupConfig(organizationId: string): Promise<ApiResponse<WhatsAppEmbeddedSignupConfig>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CONFIG(organizationId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch embedded signup configuration',
      };
    }
  }

  /**
   * Get organization's own embedded signup configuration (no inheritance)
   */
  async getOwnEmbeddedSignupConfig(): Promise<ApiResponse<WhatsAppEmbeddedSignupConfig>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CONFIG_CRUD);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch embedded signup configuration',
      };
    }
  }

  /**
   * Create embedded signup configuration
   * Only for Master Admin / Super Admin / Service Owner
   */
  async createEmbeddedSignupConfig(request: CreateEmbeddedSignupConfigRequest): Promise<ApiResponse<WhatsAppEmbeddedSignupConfig>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CONFIG_CRUD, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Embedded signup configuration created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create embedded signup configuration',
      };
    }
  }

  /**
   * Update embedded signup configuration
   * Only for Master Admin / Super Admin / Service Owner
   */
  // The server updates/deletes the CURRENT organization's config at PUT/DELETE
  // /config — there is no /config/{id} write route (that path is a master-only GET
  // by organization id), so these used to answer 405. `_id` is kept for callers.
  async updateEmbeddedSignupConfig(_id: string, request: UpdateEmbeddedSignupConfigRequest): Promise<ApiResponse<WhatsAppEmbeddedSignupConfig>> {
    try {
      const response = await api.put(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CONFIG_CRUD, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Embedded signup configuration updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update embedded signup configuration',
      };
    }
  }

  /**
   * Delete embedded signup configuration
   * Only for Master Admin / Super Admin / Service Owner
   */
  async deleteEmbeddedSignupConfig(_id: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CONFIG_CRUD);
      return {
        success: response.data.success,
        message: response.data.message || 'Embedded signup configuration deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete embedded signup configuration',
      };
    }
  }

  /**
   * Process embedded signup callback from Facebook
   * Exchanges authorization code for access token and creates WhatsApp config
   */
  async processEmbeddedSignupCallback(params: {
    businessOrganizationId: string;
    embeddedSignupConfigId: string;
    code: string;
    wabaId: string;
    phoneNumberId?: string;
  }): Promise<ApiResponse<WhatsAppConfig>> {
    try {
      const { businessOrganizationId, embeddedSignupConfigId, code, wabaId, phoneNumberId } = params;

      const response = await api.post(
        `${API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_CALLBACK}?businessOrganizationId=${businessOrganizationId}&embeddedSignupConfigId=${embeddedSignupConfigId}`,
        {
          code,
          waba_id: wabaId,
          phone_number_id: phoneNumberId,
        }
      );

      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'WhatsApp account connected successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to process embedded signup callback',
      };
    }
  }

  /**
   * Resync WhatsApp configuration from Meta
   * Syncs phone numbers and templates from the WABA
   */
  async resyncFromMeta(organizationId?: string): Promise<ApiResponse<WhatsAppResyncResponse>> {
    try {
      const params = organizationId ? { organizationId } : {};
      const response = await api.post(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_RESYNC, null, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Resync completed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to resync from Meta',
      };
    }
  }

  /**
   * Disconnect WhatsApp integration
   * Deactivates or deletes WhatsApp configuration based on deleteData flag
   */
  async disconnectIntegration(organizationId?: string, deleteData: boolean = false): Promise<ApiResponse<WhatsAppDisconnectResponse>> {
    try {
      const params: { organizationId?: string; deleteData?: boolean } = {};
      if (organizationId) params.organizationId = organizationId;
      if (deleteData) params.deleteData = deleteData;

      const response = await api.post(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_DISCONNECT, null, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'WhatsApp integration disconnected successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to disconnect WhatsApp integration',
      };
    }
  }

  /**
   * Reactivate WhatsApp integration
   * Requires a new access token from Meta (user needs to re-authenticate via embedded signup)
   */
  async reactivateIntegration(accessToken: string, organizationId?: string): Promise<ApiResponse<WhatsAppReactivateResponse>> {
    try {
      const params: { organizationId?: string; accessToken: string } = { accessToken };
      if (organizationId) params.organizationId = organizationId;

      const response = await api.post(API_ENDPOINTS.WHATSAPP_EMBEDDED_SIGNUP_REACTIVATE, null, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'WhatsApp integration reactivated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to reactivate WhatsApp integration',
      };
    }
  }

  // ==================== Templates ====================

  /**
   * Get templates with pagination and filtering
   */
  async getTemplates(params?: {
    page?: number;
    size?: number;
    status?: string;
    organizationId?: string;
  }): Promise<ApiResponse<PaginatedResponse<WhatsAppTemplate>>> {
    try {
      // Backend pages are 0-based; the UI is 1-based. Convert or page 1 silently
      // returns the second page (and an org with <=pageSize templates shows none).
      const backendParams = params
        ? { ...params, page: params.page !== undefined ? Math.max(0, params.page - 1) : 0 }
        : {};
      const response = await api.get(API_ENDPOINTS.WHATSAPP_TEMPLATES, { params: backendParams });

      // Transform backend PageResponse to PaginatedResponse.
      // Backend sends templateName (camelCase); the UI/type use `name`, so map
      // it or the template name renders blank.
      const backendData = response.data.data;
      const paginatedData: PaginatedResponse<WhatsAppTemplate> = {
        data: (backendData.content || []).map((t: any) => ({
          ...t,
          name: t.name ?? t.templateName,
        })),
        total: backendData.totalElements || 0,
        page: backendData.page || 0,
        perPage: backendData.size || 10,
        totalPages: backendData.totalPages || 0,
      };

      return {
        success: response.data.success,
        data: paginatedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch templates',
      };
    }
  }

  /**
   * Get approved templates only
   */
  async getApprovedTemplates(): Promise<ApiResponse<WhatsAppTemplate[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_TEMPLATE_APPROVED);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch approved templates',
      };
    }
  }

  /**
   * Get template by ID
   */
  async getTemplate(id: string): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_TEMPLATE_DETAIL(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch template',
      };
    }
  }

  /**
   * Upload a template header media file (image/video/document) to the assets
   * bucket and return its public URL for use as headerMediaUrl on create.
   */
  async uploadHeaderMedia(file: File): Promise<ApiResponse<{ url: string }>> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post(API_ENDPOINTS.WHATSAPP_TEMPLATE_HEADER_MEDIA, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return {
        success: response.data.success,
        data: response.data.data,
        error: response.data.success ? undefined : (response.data.message || 'Failed to upload media'),
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to upload header media',
      };
    }
  }

  /**
   * Create new template
   */
  async createTemplate(request: CreateTemplateRequest): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_TEMPLATES, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Template created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create template',
      };
    }
  }

  async updateTemplate(id: string, request: CreateTemplateRequest): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.put(API_ENDPOINTS.WHATSAPP_TEMPLATE_DETAIL(id), request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Template updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update template',
      };
    }
  }

  /**
   * Update template status
   */
  async updateTemplateStatus(id: string, status: string): Promise<ApiResponse<WhatsAppTemplate>> {
    try {
      const response = await api.put(API_ENDPOINTS.WHATSAPP_TEMPLATE_STATUS(id), { status });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Template status updated successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update template status',
      };
    }
  }

  /**
   * Delete template
   */
  async deleteTemplate(id: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.delete(API_ENDPOINTS.WHATSAPP_TEMPLATE_DETAIL(id));
      return {
        success: response.data.success,
        message: response.data.message || 'Template deleted successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete template',
      };
    }
  }

  /**
   * Get template statistics
   */
  async getTemplateStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_TEMPLATE_STATS);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch template stats',
      };
    }
  }

  /**
   * Sync templates from Meta/WhatsApp
   * Fetches latest templates from Meta and updates local database
   */
  async syncTemplates(organizationId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_TEMPLATE_SYNC, null, {
        params: { organizationId }
      });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Templates synced successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to sync templates from Meta',
      };
    }
  }

  // ==================== Campaigns ====================

  /**
   * Get campaigns with pagination and filtering
   */
  async getCampaigns(params?: {
    page?: number;
    size?: number;
    status?: string;
    organizationId?: string;
  }): Promise<ApiResponse<PaginatedResponse<WhatsAppCampaign>>> {
    try {
      // Backend pages are 0-based; UI is 1-based.
      const campaignParams = params
        ? { ...params, page: params.page !== undefined ? Math.max(0, params.page - 1) : 0 }
        : {};
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CAMPAIGNS, { params: campaignParams });

      // Transform backend PageResponse to PaginatedResponse
      const backendData = response.data.data;
      const paginatedData: PaginatedResponse<WhatsAppCampaign> = {
        data: backendData.content || [],
        total: backendData.totalElements || 0,
        page: backendData.page || 0,
        perPage: backendData.size || 10,
        totalPages: backendData.totalPages || 0,
      };

      return {
        success: response.data.success,
        data: paginatedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch campaigns',
      };
    }
  }

  /**
   * Get campaign by ID
   */
  async getCampaign(id: string): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CAMPAIGN_DETAIL(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch campaign',
      };
    }
  }

  /**
   * Create new campaign
   */
  /** Send one rendered template message to a test number (campaign builder). */
  async testSendCampaign(req: { templateId: string; toNumber: string; variables: Record<string, string> }): Promise<ApiResponse<string>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/test-send`, req);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Test send failed' };
    }
  }

  /** Per-recipient delivery breakdown for a campaign (optional status filter). */
  async getCampaignRecipients(campaignId: string, opts: { status?: string; search?: string; page?: number; size?: number } = {}): Promise<ApiResponse<any>> {
    try {
      const { status, search, page = 0, size = 50 } = opts;
      const response = await api.get(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/${campaignId}/recipients`, {
        params: { ...(status && status !== 'ALL' ? { status } : {}), ...(search ? { search } : {}), page, size },
      });
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch recipients' };
    }
  }

  /**
   * Read an uploaded CSV or XLSX WITHOUT creating anything.
   *
   * Returns header-keyed ROWS, not recipients — which column holds the number, and
   * which fills which template variable, stays the user's decision. A parser that
   * guessed would be wrong exactly when a sheet is unusual, and silently.
   *
   * The server reads it because a browser cannot open XLSX, and most lists arrive
   * as XLSX. Nothing is stored by this call.
   */
  async parseRecipientFile(file: File): Promise<ApiResponse<{
    headers: string[]; rows: Record<string, string>[]; totalRows: number; truncated: boolean;
  }>> {
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await api.post(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/recipients/parse`, form);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Could not read that file' };
    }
  }

  /**
   * Drop one person into a standing campaign.
   *
   * Returns PENDING, not sent — the scheduler wakes the campaign within a minute.
   * Said plainly because the caller is usually deciding whether to chase it.
   */
  async enrolRecipient(campaignId: string, body: { phone: string; variables?: Record<string, string>; contactId?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/${campaignId}/enrol`, body);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to enrol' };
    }
  }

  /**
   * Add a whole file of people to a standing campaign.
   *
   * Same recipient shape the campaign builder already produces from a parsed
   * CSV, so the file goes through the identical parse and column mapping.
   * Reports enrolled / skipped / failed separately — a number already waiting
   * to be sent is skipped rather than queued twice.
   */
  async enrolRecipientsBulk(
    campaignId: string,
    recipients: Array<{ phone: string; variables?: Record<string, string> }>,
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(
        `${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/${campaignId}/enrol/bulk`, { recipients });
      return response.data;
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Could not add that file' };
    }
  }

  /**
   * Exactly what one recipient was sent — the stored message, never re-rendered.
   *
   * Re-rendering would answer "what would we send now", which is a different question
   * and wrong the moment the template is edited; the reason to open this is usually
   * that somebody is disputing what they received.
   */
  async getSentCopy(campaignId: string, recipientId: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/${campaignId}/recipients/${recipientId}/sent`);
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load the sent copy' };
    }
  }

  /** WABA deliverability health (recent cap-rate + Meta quality rating) for the cooldown guard. */
  async getWabaHealth(phoneNumberId?: string): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/waba-health`, { params: phoneNumberId ? { phoneNumberId } : {} });
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch WABA health' };
    }
  }

  /** Resend a finished campaign to only its FAILED recipients (creates + starts a new campaign). */
  async resendFailed(
    campaignId: string,
    opts?: { target?: string; errorCode?: string; mode?: string; value?: number; sampling?: string }
  ): Promise<ApiResponse<any>> {
    try {
      const response = await api.post(`${API_ENDPOINTS.WHATSAPP_CAMPAIGNS}/${campaignId}/resend-failed`, opts || undefined);
      return { success: response.data.success, data: response.data.data, message: response.data.message };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to resend' };
    }
  }

  async createCampaign(request: CreateCampaignRequest): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CAMPAIGNS, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Campaign created successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to create campaign',
      };
    }
  }

  /**
   * Change an existing campaign.
   *
   * PATCH, not PUT: the body carries only the fields that actually changed, and
   * only those the campaign's own `editableFields` offered. A field the current
   * status will not accept comes back as a 400 whose `message` is written for a
   * person — it is passed straight through rather than replaced with "Update
   * failed", because the server's sentence names the field and this one cannot.
   */
  async updateCampaign(id: string, changes: UpdateCampaignRequest): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.patch(API_ENDPOINTS.WHATSAPP_CAMPAIGN_UPDATE(id), changes);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Campaign updated',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update campaign',
      };
    }
  }

  /**
   * Start campaign
   */
  async startCampaign(id: string): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CAMPAIGN_START(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Campaign started successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to start campaign',
      };
    }
  }

  /**
   * Pause campaign
   */
  async pauseCampaign(id: string): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CAMPAIGN_PAUSE(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Campaign paused successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to pause campaign',
      };
    }
  }

  /**
   * Cancel campaign
   */
  async cancelCampaign(id: string): Promise<ApiResponse<WhatsAppCampaign>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CAMPAIGN_CANCEL(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Campaign cancelled successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to cancel campaign',
      };
    }
  }

  /** Campaign insights: funnel + error analysis + quick-reply button clicks. */
  async getCampaignInsights(id: string): Promise<ApiResponse<CampaignInsights>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CAMPAIGN_INSIGHTS(id));
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load campaign insights' };
    }
  }

  /** Contacts who responded to a campaign; pass a button label (e.g. "Interested") or omit for all repliers. */
  async getCampaignResponders(id: string, button?: string): Promise<ApiResponse<Array<{ phone: string; name?: string; respondedAt?: number }>>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CAMPAIGN_RESPONDERS(id), {
        params: button ? { button } : undefined,
      });
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load responders' };
    }
  }

  /** Template insights: funnel + per-button clicks + daily trend + error analysis. */
  async getTemplateInsights(id: string, days = 30): Promise<ApiResponse<TemplateInsights>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_TEMPLATE_INSIGHTS(id, days));
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to load template insights' };
    }
  }

  /**
   * Get campaign statistics
   */
  async getCampaignStats(): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CAMPAIGN_STATS);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch campaign stats',
      };
    }
  }

  // ==================== Messages ====================

  /**
   * Get all messages with pagination
   */
  async getMessages(params?: {
    page?: number;
    size?: number;
    organizationId?: string;
    direction?: string;
    status?: string;
    fromMillis?: number;
    toMillis?: number;
  }): Promise<ApiResponse<PaginatedResponse<WhatsAppMessage>>> {
    try {
      // Backend pages are 0-based; UI is 1-based.
      const messageParams = params
        ? { ...params, page: params.page !== undefined ? Math.max(0, params.page - 1) : 0 }
        : {};
      const response = await api.get(API_ENDPOINTS.WHATSAPP_MESSAGES, { params: messageParams });

      // Transform backend PageResponse to PaginatedResponse
      const backendData = response.data.data;
      const paginatedData: PaginatedResponse<WhatsAppMessage> = {
        data: backendData.content || [],
        total: backendData.totalElements || 0,
        page: backendData.page || 0,
        perPage: backendData.size || 10,
        totalPages: backendData.totalPages || 0,
      };

      return {
        success: response.data.success,
        data: paginatedData,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch messages',
      };
    }
  }

  /**
   * Get messages for specific contact
   */
  async getContactMessages(contactId: string): Promise<ApiResponse<WhatsAppMessage[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_CONTACT_MESSAGES(contactId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch messages',
      };
    }
  }

  /**
   * Get message by ID
   */
  async getMessage(id: string): Promise<ApiResponse<WhatsAppMessage>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_MESSAGE_DETAIL(id));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch message',
      };
    }
  }

  /**
   * Send message to contact
   */
  async sendMessage(request: SendMessageRequest): Promise<ApiResponse<WhatsAppMessage>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_MESSAGES, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Message sent successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send message',
      };
    }
  }

  /**
   * Universal message send endpoint - supports all message types
   * Types: TEXT, TEMPLATE, IMAGE, DOCUMENT, AUDIO, VIDEO, LOCATION, INTERACTIVE, REACTION
   */
  async sendMessageUniversal(request: WhatsAppSendMessageRequest): Promise<ApiResponse<WhatsAppMessage>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_MESSAGE_SEND, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Message sent successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send message',
      };
    }
  }

  /**
   * Convenience endpoint for sending text messages
   */
  async sendTextMessage(request: WhatsAppSendTextRequest): Promise<ApiResponse<WhatsAppMessage>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_MESSAGE_SEND_TEXT, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Text message sent successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send text message',
      };
    }
  }

  /**
   * Convenience endpoint for sending template messages
   */
  async sendTemplateMessage(request: WhatsAppSendTemplateRequest): Promise<ApiResponse<WhatsAppMessage>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_MESSAGE_SEND_TEMPLATE, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Template message sent successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to send template message',
      };
    }
  }

  /**
   * Mark a message as read
   */
  async markMessageAsRead(messageId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_MESSAGE_MARK_READ(messageId));
      return {
        success: response.data.success,
        message: response.data.message || 'Message marked as read',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to mark message as read',
      };
    }
  }

  // ==================== Contacts ====================
  // NOTE: WhatsApp contacts use the unified Contact API with channel filtering
  // See contactsService.getContacts({ channel: 'whatsapp' })

  // ==================== Reports ====================

  /**
   * Get hourly reports
   */
  async getHourlyReports(params: {
    startDate: number; // Epoch milliseconds
    endDate: number; // Epoch milliseconds
  }): Promise<ApiResponse<WhatsAppHourlyReport[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_REPORTS_HOURLY, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch hourly reports',
      };
    }
  }

  /**
   * Get latest hourly reports
   */
  async getLatestReports(limit: number = 24): Promise<ApiResponse<WhatsAppHourlyReport[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_REPORTS_LATEST, {
        params: { limit }
      });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch latest reports',
      };
    }
  }

  /**
   * Get aggregated report statistics
   */
  async getReportStats(params: {
    startDate: number; // Epoch milliseconds
    endDate: number; // Epoch milliseconds
  }): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_REPORTS_STATS, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch report stats',
      };
    }
  }

  /**
   * Rich WhatsApp analytics (totals, rates, by-type, daily series, failures, campaigns)
   * computed live from messages + campaigns. Date range optional (defaults to last 30d).
   */
  async getAnalytics(params?: { startDate?: number; endDate?: number; timezone?: string }): Promise<ApiResponse<any>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_REPORTS_ANALYTICS, { params });
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch analytics',
      };
    }
  }

  /** Per-contact unread inbound counts (for conversation-list badges). */
  async getUnreadCounts(includeAutoReplies: boolean = true): Promise<ApiResponse<Record<string, number>>> {
    try {
      const response = await api.get('/v1/whatsapp/messages/unread-counts', { params: { includeAutoReplies } });
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch unread counts' };
    }
  }

  /** Mark a conversation's inbound messages as read (clears unread badge). */
  async markConversationRead(contactId: string): Promise<ApiResponse<number>> {
    try {
      const response = await api.post('/v1/whatsapp/messages/mark-read', null, { params: { contactId } });
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to mark read' };
    }
  }

  // ---- Lead auto-assignment ----
  async getLeadConfigs(): Promise<ApiResponse<any[]>> {
    try {
      const response = await api.get('/v1/whatsapp/lead-config');
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to fetch lead config' };
    }
  }

  async saveLeadConfig(req: { phoneNumberId?: string | null; strategy: string; enabled: boolean; handlerUserIds: string[] }): Promise<ApiResponse<any>> {
    try {
      const response = await api.put('/v1/whatsapp/lead-config', req);
      return { success: response.data.success, data: response.data.data };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to save lead config' };
    }
  }

  async reassignLead(contactId: string, handlerUserId: string): Promise<ApiResponse<void>> {
    try {
      const response = await api.post('/v1/whatsapp/lead-config/reassign', { contactId, handlerUserId });
      return { success: response.data.success };
    } catch (error: any) {
      return { success: false, error: error.response?.data?.message || 'Failed to reassign' };
    }
  }

  // ==================== Partner / Credit Line (Admin) ====================

  /**
   * Get all WABA registrations with credit line status
   * Admin only endpoint
   */
  async getWABARegistrations(): Promise<ApiResponse<WABARegistration[]>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_PARTNER_REGISTRATIONS);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch WABA registrations',
      };
    }
  }

  /**
   * Get a single WABA registration by ID
   * Admin only endpoint
   */
  async getWABARegistration(registrationId: string): Promise<ApiResponse<WABARegistration>> {
    try {
      const response = await api.get(API_ENDPOINTS.WHATSAPP_PARTNER_REGISTRATION_DETAIL(registrationId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message,
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch WABA registration',
      };
    }
  }

  /**
   * Link credit line to a WABA
   * Admin only endpoint
   */
  async linkCreditLine(request: LinkCreditLineRequest): Promise<ApiResponse<LinkCreditLineResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_PARTNER_LINK_CREDIT_LINE, request);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Credit line linked successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to link credit line',
      };
    }
  }

  /**
   * Link credit line by registration ID
   * Convenience method that fetches wabaId and phoneNumberId from registration
   * Admin only endpoint
   */
  async linkCreditLineByRegistration(registrationId: string): Promise<ApiResponse<LinkCreditLineResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_PARTNER_LINK_CREDIT_LINE_BY_REGISTRATION(registrationId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Credit line linked successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to link credit line',
      };
    }
  }

  // ==================== Webhook Subscription (Admin) ====================

  /**
   * Subscribe webhooks for the default WABA configuration
   * Admin only endpoint
   */
  async subscribeWebhooks(): Promise<ApiResponse<WebhookSubscriptionResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS);
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Webhooks subscribed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to subscribe webhooks',
      };
    }
  }

  /**
   * Subscribe webhooks for a specific config by ID
   * Admin only endpoint
   */
  async subscribeWebhooksByConfigId(configId: string): Promise<ApiResponse<WebhookSubscriptionResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS_BY_ID(configId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Webhooks subscribed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to subscribe webhooks',
      };
    }
  }

  /**
   * Subscribe webhooks for a specific WABA by WABA ID
   * Admin only endpoint
   */
  async subscribeWebhooksByWabaId(wabaId: string): Promise<ApiResponse<WebhookSubscriptionResponse>> {
    try {
      const response = await api.post(API_ENDPOINTS.WHATSAPP_CONFIG_SUBSCRIBE_WEBHOOKS_BY_WABA(wabaId));
      return {
        success: response.data.success,
        data: response.data.data,
        message: response.data.message || 'Webhooks subscribed successfully',
      };
    } catch (error: any) {
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to subscribe webhooks',
      };
    }
  }
}

export const whatsappService = new WhatsAppService();
